import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperIcon,
  MatStepperNext,
  MatStepperPrevious,
} from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, tap, throwError } from 'rxjs';

import { User } from '../../models/user.model';
import { UnitResponseModel } from '../units/units.model';
import { UnitService } from '../units/services/unit.service';
import { UserService } from '../users/services/user.service';

@Component({
  selector: 'app-settings-actions-move-user',
  standalone: true,
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperIcon,
    MatStepperNext,
    MatStepperPrevious,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    FormsModule,
  ],
  templateUrl: './settings-actions-move-user.component.html',
  styleUrl: './settings-actions-move-user.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsActionsMoveUserComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _userService = inject(UserService);
  private readonly _units = inject(UnitService);
  private readonly _snackbar = inject(MatSnackBar);

  // State
  protected readonly targetUser = signal<User | null>(null);
  protected readonly units = signal<UnitResponseModel[]>([]);
  protected readonly selectedUnitId = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);
  protected readonly maxStepReached = signal(0);

  @ViewChild(MatStepper) stepper!: MatStepper;

  // Derived
  protected readonly availableUnits = computed(() => {
    const u = this.targetUser();
    if (!u) return this.units();
    return this.units().filter(
      (unit) => unit._id !== u.rootUnitId && !unit.deleteAt,
    );
  });

  protected readonly sourceUnit = computed(() => {
    const u = this.targetUser();
    if (!u) return null;
    return this.units().find((unit) => unit._id === u.rootUnitId) ?? null;
  });

  protected readonly selectedUnit = computed(() => {
    const id = this.selectedUnitId();
    return this.units().find((unit) => unit._id === id) ?? null;
  });

  ngOnInit(): void {
    const userId = this._route.snapshot.paramMap.get('userId');
    if (!userId) {
      this._router.navigate(['/settings/users']);
      return;
    }

    // Load user and units in parallel
    this._userService.getById(userId).subscribe({
      next: (user: User) => this.targetUser.set(user),
      error: () => {
        this._snackbar.open('Gebruiker niet gevonden', 'OK');
        this._router.navigate(['/settings/users']);
      },
    });

    this._units.getByUser().subscribe({
      next: (units: UnitResponseModel[]) => this.units.set(units),
    });
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.maxStepReached.update((current) =>
      Math.max(current, event.selectedIndex),
    );
  }

  submit(): void {
    const userId = this.targetUser()?._id;
    const targetUnitId = this.selectedUnitId();
    if (!userId || !targetUnitId) return;

    this.isSubmitting.set(true);

    this._userService
      .moveUser(userId, { targetUnitId })
      .pipe(
        tap(() => {
          this._snackbar.open(
            `Gebruiker ${this.targetUser()?.displayName} is verplaatst`,
            'OK',
          );
          this._router.navigate(['/settings/users']);
        }),
        catchError((error) => {
          this._snackbar.open(
            'Er is iets misgegaan bij het verplaatsen. Probeer het later nog eens.',
            'OK',
          );
          return throwError(() => error);
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  cancel(): void {
    this._router.navigate(['/settings/users']);
  }
}
